import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agrogestao } from "@/api/agrogestaoClient";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
    ArrowLeft, TrendingUp, TrendingDown, DollarSign,
    MapPin, Calendar, Package, BarChart3, AlertCircle, ShoppingCart
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

export default function SafraDetalhes() {
    const { id } = useParams();
    const navigate = useNavigate();
    const propriedadeId = localStorage.getItem("propriedade_selecionada");

    const { data: safra, isLoading: loadingSafra } = useQuery({
        queryKey: ['safra', id],
        queryFn: () => agrogestao.entities.Safra.filter({ id }).then(r => r[0]),
        enabled: !!id
    });

    const { data: lancamentos = [], isLoading: loadingLancamentos } = useQuery({
        queryKey: ['lancamentos', 'safra', id],
        queryFn: () => agrogestao.entities.Lancamento.filter({ safra_id: id }),
        enabled: !!id
    });

    const { data: movInsumos = [] } = useQuery({
        queryKey: ['mov_insumos', 'safra', id],
        queryFn: () => agrogestao.entities.MovimentacaoInsumo.filter({ safra_id: id }),
        enabled: !!id
    });

    const { data: movGraos = [] } = useQuery({
        queryKey: ['mov_graos', 'safra', id],
        queryFn: () => agrogestao.entities.MovimentacaoGrao.filter({ safra_id: id }),
        enabled: !!id
    });

    const { data: talhoes = [] } = useQuery({
        queryKey: ['talhoes', propriedadeId],
        queryFn: () => agrogestao.entities.Talhao.filter({ propriedade_id: propriedadeId }),
        enabled: !!propriedadeId
    });

    if (loadingSafra || loadingLancamentos) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#1B4332] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!safra) {
        return (
            <div className="p-8 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold">Safra não encontrada</h2>
                <Button onClick={() => navigate('/Safras')} className="mt-4">Voltar</Button>
            </div>
        );
    }

    // CÁLCULOS DINÂMICOS E INTEGRADOS
    const custosFinanceiros = lancamentos.filter(l => l.tipo === 'despesa').reduce((acc, l) => acc + (l.valor || 0), 0);
    const custosInsumos = movInsumos.reduce((acc, m) => acc + (Number(m.custo_total) || 0), 0);
    const custosTotal = custosFinanceiros + custosInsumos;

    const receitasVendas = movGraos.filter(m => m.tipo === 'saida').reduce((acc, m) => acc + (Number(m.valor_total) || 0), 0);
    const receitasFinanceiras = lancamentos.filter(l => l.tipo === 'receita').reduce((acc, l) => acc + (l.valor || 0), 0);
    const receitasTotal = receitasVendas + receitasFinanceiras;

    const resultado = receitasTotal - custosTotal;
    const margem = receitasTotal > 0 ? (resultado / receitasTotal) * 100 : 0;
    const area = Number(safra.area_plantada) || 0;
    const custoPorHa = area > 0 ? custosTotal / area : 0;

    // Produção e Qualidade
    const entradasGrao = movGraos.filter(m => m.tipo === 'entrada');
    const producaoBruta = (entradasGrao.reduce((acc, m) => acc + (Number(m.peso_bruto) || Number(m.quantidade) || 0), 0)) / 60;
    const producaoLiquida = (entradasGrao.reduce((acc, m) => acc + (Number(m.peso_liquido) || Number(m.quantidade) || 0), 0)) / 60;
    const quebraTotal = producaoBruta - producaoLiquida;
    const produtividadeMedia = area > 0 ? producaoLiquida / area : 0;

    // Comercialização
    const totalVendido = (movGraos.filter(m => m.tipo === 'saida').reduce((acc, m) => acc + (Number(m.quantidade) || 0), 0)) / 60;
    const precoMedioVenda = totalVendido > 0 ? receitasVendas / totalVendido : 0;
    const emEstoque = producaoLiquida - totalVendido;

    // Agrupamento por Talhão
    const producaoPorTalhaoRaw = entradasGrao.reduce((acc, m) => {
        if (!m.talhao_id) return acc;
        if (!acc[m.talhao_id]) acc[m.talhao_id] = { bruto: 0, liquido: 0, count: 0 };
        acc[m.talhao_id].bruto += (Number(m.peso_bruto) || Number(m.quantidade) || 0) / 60;
        acc[m.talhao_id].liquido += (Number(m.peso_liquido) || Number(m.quantidade) || 0) / 60;
        acc[m.talhao_id].count++;
        return acc;
    }, {});

    const formatCurrency = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
    const formatNumber = (v) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v || 0);

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-20">
            {/* Header Brutalista */}
            <div className="bg-[#1B4332] text-white pt-12 pb-32 px-6 lg:px-12 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4A373] opacity-10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
                <div className="max-w-7xl mx-auto relative z-10">
                    <button onClick={() => navigate('/Safras')} className="flex items-center gap-2 text-stone-300 hover:text-white transition-colors mb-8 group">
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        Voltar para Safras
                    </button>
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <Badge className="bg-[#D4A373] text-[#1B4332] hover:bg-[#D4A373] rounded-none px-4 py-1 font-bold">{safra.cultura.toUpperCase()}</Badge>
                                <Badge variant="outline" className="text-white border-white/30 rounded-none uppercase px-4 py-1">{safra.status.replace('_', ' ')}</Badge>
                            </div>
                            <h1 className="text-5xl lg:text-7xl font-black tracking-tighter leading-none mb-4">
                                {safra.nome.split(' ').map((word, i) => (<span key={i} className={i % 2 === 0 ? "text-white" : "text-[#D4A373]"}>{word}{' '}</span>))}
                            </h1>
                            <div className="flex flex-wrap gap-6 text-stone-300 font-medium">
                                <div className="flex items-center gap-2"><MapPin className="w-5 h-5 text-[#D4A373]" />{area} ha plantados</div>
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-[#D4A373]" />
                                    {safra.data_inicio ? format(parseISO(safra.data_inicio), "MMM yyyy", { locale: ptBR }) : '-'} —
                                    {safra.data_fim ? format(parseISO(safra.data_fim), " MMM yyyy", { locale: ptBR }) : ' Em aberto'}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col items-start lg:items-end">
                            <p className="text-stone-400 text-sm font-bold uppercase tracking-widest mb-1">Resultado Projetado</p>
                            <p className={`text-4xl lg:text-6xl font-black ${resultado >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCurrency(resultado)}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-12 -mt-20 relative z-20">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    {[
                        { label: 'Custo Total', value: formatCurrency(custosTotal), icon: TrendingDown, color: 'text-red-600', bg: 'bg-white' },
                        { label: 'Receita Est.', value: formatCurrency(receitasTotal), icon: TrendingUp, color: 'text-green-600', bg: 'bg-white' },
                        { label: 'Custo/ha', value: formatCurrency(custoPorHa), icon: DollarSign, color: 'text-[#1B4332]', bg: 'bg-[#D4A373]/20' },
                        { label: 'Margem Líquida', value: `${margem.toFixed(1)}%`, icon: BarChart3, color: 'text-[#1B4332]', bg: 'bg-white' }
                    ].map((kpi, i) => (
                        <Card key={i} className={`${kpi.bg} border-none shadow-xl rounded-none transition-transform hover:-translate-y-1`}>
                            <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-stone-500 text-xs font-bold uppercase">{kpi.label}</p>
                                    <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                                </div>
                                <p className={`text-2xl font-black text-stone-900`}>{kpi.value}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-8">
                        <Card className="border-none shadow-xl rounded-none bg-white overflow-hidden">
                            <CardHeader className="bg-stone-50 border-b border-stone-100">
                                <CardTitle className="text-xl font-black flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-[#1B4332]" />
                                    PRODUTIVIDADE POR TALHÃO
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent border-stone-100">
                                            <TableHead className="font-bold uppercase text-xs">Talhão</TableHead>
                                            <TableHead className="font-bold uppercase text-xs">Área</TableHead>
                                            <TableHead className="text-right font-bold uppercase text-xs">Produção Líquida</TableHead>
                                            <TableHead className="text-right font-bold uppercase text-xs">Rendimento</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {Object.entries(producaoPorTalhaoRaw).map(([tId, data]) => {
                                            const talhao = talhoes.find(t => t.id === tId);
                                            const tArea = Number(talhao?.area_hectares) || 0;
                                            const rendimento = tArea > 0 ? data.liquido / tArea : 0;
                                            return (
                                                <TableRow key={tId} className="border-stone-50">
                                                    <TableCell className="font-bold text-stone-800">{talhao?.nome || 'Não Identificado'}</TableCell>
                                                    <TableCell className="text-stone-500">{tArea} ha</TableCell>
                                                    <TableCell className="text-right font-black">{formatNumber(data.liquido)} scs</TableCell>
                                                    <TableCell className="text-right"><Badge className="bg-[#1B4332] rounded-none">{formatNumber(rendimento)} scs/ha</Badge></TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {Object.keys(producaoPorTalhaoRaw).length === 0 && (
                                            <TableRow><TableCell colSpan={4} className="text-center py-12 text-stone-400 font-medium italic">Nenhuma movimentação de colheita vinculada a talhões.</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>

                        <Card className="border-none shadow-xl rounded-none bg-white">
                            <CardHeader className="border-b border-stone-100 flex flex-row items-center justify-between">
                                <CardTitle className="text-xl font-black flex items-center gap-2">
                                    <DollarSign className="w-5 h-5 text-[#1B4332]" />
                                    FLUXO DE CAIXA DA SAFRA
                                </CardTitle>
                                <Badge className="bg-stone-100 text-stone-600 border-none rounded-none">{lancamentos.length} Registros</Badge>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="hover:bg-transparent border-stone-100">
                                            <TableHead className="font-bold uppercase text-xs">Data</TableHead>
                                            <TableHead className="font-bold uppercase text-xs">Descrição</TableHead>
                                            <TableHead className="text-right font-bold uppercase text-xs">Valor</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {lancamentos.sort((a, b) => new Date(b.data) - new Date(a.data)).slice(0, 10).map((l) => (
                                            <TableRow key={l.id} className="border-stone-50 hover:bg-stone-50/50">
                                                <TableCell className="text-stone-500 font-medium">{format(parseISO(l.data), "dd/MM/yy")}</TableCell>
                                                <TableCell className="font-bold text-stone-800">
                                                    <div className="flex items-center gap-2">
                                                        {l.tipo === 'receita' ? <TrendingUp className="w-3 h-3 text-green-500" /> : <TrendingDown className="w-3 h-3 text-red-500" />}
                                                        {l.descricao}
                                                    </div>
                                                </TableCell>
                                                <TableCell className={`text-right font-black ${l.tipo === 'receita' ? 'text-green-600' : 'text-red-600'}`}>{l.tipo === 'receita' ? '+' : '-'}{formatCurrency(l.valor)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </div>

                    <div className="space-y-8">
                        <Card className="border-none shadow-2xl rounded-none bg-[#1B4332] text-white">
                            <CardHeader className="pb-2"><CardTitle className="text-lg font-black uppercase tracking-widest text-[#D4A373]">Consolidado de Colheita</CardTitle></CardHeader>
                            <CardContent className="space-y-6">
                                <div className="bg-white/5 p-4 border border-white/10">
                                    <p className="text-[10px] font-black text-stone-400 uppercase mb-1">Produção Líquida Total</p>
                                    <p className="text-4xl font-black text-white">{formatNumber(producaoLiquida)} <span className="text-sm text-[#D4A373]">SCS</span></p>
                                    <div className="mt-2 flex items-center justify-between text-[10px] font-bold">
                                        <span className="text-stone-400">BRUTO: {formatNumber(producaoBruta)}</span>
                                        <span className="text-amber-400">QUEBRA: {formatNumber(quebraTotal)} ({((quebraTotal / producaoBruta) * 100 || 0).toFixed(1)}%)</span>
                                    </div>
                                </div>
                                <div className="bg-[#D4A373] p-4 text-[#1B4332]">
                                    <p className="text-[10px] font-black uppercase mb-1">Rendimento Médio Geral</p>
                                    <p className="text-3xl font-black italic">{formatNumber(produtividadeMedia)} <span className="text-sm ml-1 uppercase">scs/ha</span></p>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-xs font-bold uppercase text-stone-400">Perdas Técnicas</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-white/5 p-2 border border-white/5">
                                            <p className="text-[9px] text-stone-400 uppercase">Umidade</p>
                                            <p className="text-sm font-bold text-amber-500">{formatNumber(entradasGrao.reduce((acc, m) => acc + (Number(m.desconto_umidade) || 0), 0))} scs</p>
                                        </div>
                                        <div className="bg-white/5 p-2 border border-white/5">
                                            <p className="text-[9px] text-stone-400 uppercase">Impureza</p>
                                            <p className="text-sm font-bold text-amber-500">{formatNumber(entradasGrao.reduce((acc, m) => acc + (Number(m.desconto_impureza) || 0), 0))} scs</p>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-none shadow-xl rounded-none bg-white">
                            <CardHeader className="border-b border-stone-100">
                                <CardTitle className="text-lg font-black uppercase flex items-center gap-2">
                                    <ShoppingCart className="w-5 h-5 text-[#1B4332]" /> COMERCIALIZAÇÃO
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-6 space-y-6">
                                <div className="flex justify-between items-end border-b border-stone-100 pb-4">
                                    <div>
                                        <p className="text-[10px] font-black text-stone-400 uppercase">Volume Vendido</p>
                                        <p className="text-2xl font-black text-stone-800">{formatNumber(totalVendido)} <span className="text-xs">SCS</span></p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[10px] font-black text-stone-400 uppercase">Preço Médio</p>
                                        <p className="text-xl font-black text-green-600">{formatCurrency(precoMedioVenda)}</p>
                                    </div>
                                </div>
                                <div className="pt-2">
                                    <div className="flex justify-between text-[10px] font-black uppercase mb-1">
                                        <span className="text-stone-500">Saldo em Armazém</span>
                                        <span className="text-[#1B4332]">{formatNumber(emEstoque)} scs</span>
                                    </div>
                                    <Progress value={(emEstoque / producaoLiquida) * 100 || 0} className="h-2 rounded-none bg-stone-100" />
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-none shadow-xl rounded-none bg-white">
                            <CardHeader className="pb-2"><CardTitle className="text-xs font-black uppercase text-stone-400">Eficiência de Custo</CardTitle></CardHeader>
                            <CardContent>
                                <div className="text-center p-4 bg-stone-50 border border-stone-100">
                                    <p className="text-[10px] font-black text-stone-500 uppercase">Custo Produção p/ Saca</p>
                                    <p className="text-3xl font-black text-[#1B4332]">{producaoLiquida > 0 ? formatCurrency(custosTotal / producaoLiquida) : 'R$ 0,00'}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
